import { token } from '@terra/ui-styled-system';
import type { HighlightColor } from '../../store/proxy-store';

export type HighlightOption = {
  color: HighlightColor;
  label: string;
  description: string;
};

const PALETTE_TOKEN_PATHS = {
  red: 'colors.red.9',
  orange: 'colors.terra.9',
  yellow: 'colors.yellow.9',
  green: 'colors.green.9',
  blue: 'colors.sky.9',
  cyan: 'colors.green.10',
  purple: 'colors.purple.9',
  crimson: 'colors.crimson.9',
  gray: 'colors.gray.9',
} as const;

const PALETTE_SOFT_TOKEN_PATHS = {
  red: 'colors.red.7',
  orange: 'colors.terra.8',
  yellow: 'colors.yellow.7',
  green: 'colors.green.6',
  blue: 'colors.sky.6',
  cyan: 'colors.green.9',
  purple: 'colors.purple.5',
  crimson: 'colors.crimson.5',
  gray: 'colors.gray.6',
} as const;

type PaletteColor = keyof typeof PALETTE_TOKEN_PATHS;

function resolvePaletteColor(color: PaletteColor): string {
  return token(PALETTE_TOKEN_PATHS[color]);
}

function resolvePaletteSoftColor(color: PaletteColor): string {
  return token(PALETTE_SOFT_TOKEN_PATHS[color]);
}

function toPaletteColor(color: string): PaletteColor | undefined {
  switch (color) {
    case 'red':
    case 'orange':
    case 'yellow':
    case 'green':
    case 'blue':
    case 'cyan':
    case 'purple':
    case 'crimson':
    case 'gray':
      return color;
    default:
      return undefined;
  }
}

export const HIGHLIGHT_OPTIONS: HighlightOption[] = [
  {
    color: 'red',
    label: 'Red',
    description: 'Critical/Vulnerability',
  },
  {
    color: 'orange',
    label: 'Orange',
    description: 'Important/Warning',
  },
  {
    color: 'yellow',
    label: 'Yellow',
    description: 'Review needed',
  },
  {
    color: 'green',
    label: 'Green',
    description: 'Safe/Verified',
  },
  {
    color: 'blue',
    label: 'Blue',
    description: 'Information',
  },
  {
    color: 'purple',
    label: 'Purple',
    description: 'Custom',
  },
  {
    color: 'gray',
    label: 'Gray',
    description: 'Low priority',
  },
];

export function getHighlightHex(color: HighlightColor): string {
  return resolvePaletteColor(toPaletteColor(color) ?? 'gray');
}

export function getHighlightRowTint(color: HighlightColor): string {
  const highlightColor = getHighlightHex(color);
  return `color-mix(in srgb, ${highlightColor} 12%, transparent)`;
}

export function getPaletteColor(color: string): string {
  return resolvePaletteColor(toPaletteColor(color) ?? 'gray');
}

export function getPaletteSoftColor(color: string): string {
  return resolvePaletteSoftColor(toPaletteColor(color) ?? 'gray');
}

export function getPaletteTint(color: string, amountPercent = 18): string {
  return `color-mix(in srgb, ${getPaletteSoftColor(color)} ${amountPercent}%, transparent)`;
}

export const COMMON_TAGS = [
  'sqli',
  'xss',
  'csrf',
  'auth',
  'api',
  'interesting',
  'retest',
  'vuln',
  'info',
  'reviewed',
];
